import type { ReactNode } from "react";
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { graphql, useFragment } from "react-relay";

import { article } from "shared/data";
import { articleSponsorType } from "shared/data/article";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { Typeform } from "scmp-app/lib/typeform";
import { useReadMoreTypeform } from "scmp-app/lib/typeform/hooks";
import type { contextsContentReadMoreContextProviderContent$key } from "scmp-app/queries/__generated__/contextsContentReadMoreContextProviderContent.graphql";

type ContextValue = {
  isReadMoreClicked: boolean;
  setIsReadMoreClicked?: (isReadMoreClicked: boolean) => void;
};

const ContentReadMoreContext = createContext<ContextValue>({
  isReadMoreClicked: false,
});

type Props = {
  children: ReactNode;
  reference: contextsContentReadMoreContextProviderContent$key;
};

export const ContentReadMoreContextProvider: React.FunctionComponent<Props> = ({
  children,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment contextsContentReadMoreContextProviderContent on Content {
        __typename
        ...helpersGetArticleTypeArticle
        ...helpersCheckIsStyleArticle
        ...hooksReadMoreTypeformContent
        ... on Article {
          sponsorType
        }
      }
    `,
    reference,
  );

  const mainArticleType = getArticleType(content);
  const disabledArticleTypes = new Set<string>([
    article.types.live.entityId,
    article.types.newsletter.entityId,
    article.types.series.entityId,
    article.types.studio.entityId,
  ]);
  const disabledContentTypes = new Set<string>(["Video"]);
  const { configKey, triggerOpen, utm_content, utm_source, utm_term } = useReadMoreTypeform({
    reference: content,
  });

  const isDisableReadMore =
    disabledArticleTypes.has(mainArticleType ?? "") ||
    articleSponsorType.has(content.sponsorType ?? "") ||
    disabledContentTypes.has(content.__typename ?? "");

  const [isReadMoreClicked, setIsReadMoreClicked] = useState(isDisableReadMore);

  const setNextIsReadMoreClicked = useCallback((nextIsReadMoreClicked: boolean) => {
    startTransition(() => {
      setIsReadMoreClicked(nextIsReadMoreClicked);
    });
  }, []);

  useEffect(() => {
    if (!isReadMoreClicked || configKey) return;
    triggerOpen();
  }, [isReadMoreClicked, configKey, triggerOpen]);

  return (
    <ContentReadMoreContext.Provider
      value={{
        isReadMoreClicked,
        setIsReadMoreClicked: setNextIsReadMoreClicked,
      }}
    >
      {children}
      {configKey && (
        <Typeform
          configKey={configKey}
          utm_content={utm_content}
          utm_source={utm_source}
          utm_term={utm_term}
        />
      )}
    </ContentReadMoreContext.Provider>
  );
};
ContentReadMoreContextProvider.displayName = "ContentReadMoreContextProvider";

export const useContentReadMoreContext = () => useContext(ContentReadMoreContext);
